// Labcorp form state and constants.
// Test data is now fetched from FHIR ActivityDefinition resources via useTestCatalog hook.
// Instruction codes, microbiology sources, and specimen legend remain hardcoded
// as they are form-specific metadata, not orderable tests.

export const LABCORP_QUESTIONNAIRE_ID = 'labcorp-0800-blank-requisition'

export interface LabcorpWriteInTest {
  testNumber: string
  testName: string
}

// ---------------------------------------------------------------------------
// Instruction Codes (Laboratory Use Only row — form metadata, not FHIR tests)
// ---------------------------------------------------------------------------

export const LABCORP_INSTRUCTION_CODES = [
  { code: '998074', name: 'STAT' },
  { code: '998089', name: 'VENIPUNCTURE' },
  { code: '998439', name: 'NON LABCORP' },
  { code: '998190', name: 'VERBAL ORDER' },
  { code: '998291', name: 'CHART ORDER' },
  { code: '998922', name: 'HANDWRITTEN' },
  { code: '998218', name: '2 HR PP/2V' },
] as const

// ---------------------------------------------------------------------------
// Microbiology Sources (form-specific, not FHIR tests)
// ---------------------------------------------------------------------------

export const LABCORP_MICROBIOLOGY_SOURCES = [
  'ENDOCERVIX',
  'THROAT',
  'URINE',
  'STOOL',
  'URETHRA',
] as const

// ---------------------------------------------------------------------------
// Specimen Type Legend (display helper)
// ---------------------------------------------------------------------------

export const LABCORP_SPECIMEN_LEGEND: Record<string, string> = {
  GEL: 'SST (Gold/Red Gel)',
  LAV: 'Lavender (EDTA)',
  BLU: 'Blue (Citrate)',
  GRN: 'Green (Lithium Heparin)',
  UA: 'Urine',
  KIT: 'Kit',
}

// ---------------------------------------------------------------------------
// Form State
// ---------------------------------------------------------------------------

export interface LabcorpFormState {
  // Patient Information
  firstName: string
  middleInitial: string
  lastName: string
  sexAtBirth: string
  dateOfBirth: string
  patientId: string
  patientAddress: string
  patientPhone: string
  patientCity: string
  patientState: string
  patientZip: string

  // Provider
  physicianLastName: string
  physicianFirstName: string
  physicianNpi: string
  physicianId: string

  // Diagnosis
  diagnosisText: string

  // Primary Insurance / Billing
  primaryInsuranceCarrier: string
  primaryInsuranceId: string
  primaryGroupNumber: string
  primaryInsuranceAddress: string
  primaryInsuredName: string
  primaryRelationship: string
  primaryEmployerName: string

  // Medicaid / Workers Comp
  medicaidState: string
  physiciansProviderNumber: string
  isWorkersComp: boolean

  // Responsible Party
  policyHolderName: string
  policyHolderAddress: string
  policyHolderApt: string
  policyHolderCity: string
  policyHolderState: string
  policyHolderZip: string

  // Instruction Codes
  selectedInstructionCodes: string[]
  isPstPsc: boolean

  // Test Selection — single unified array of test codes
  selectedTests: string[]
  writeInTests: LabcorpWriteInTest[]

  // Microbiology
  microbiologySources: string[]
  microbiologyOtherSource: string

  // Clinical Information / Comments
  clinicalComments: string

  // Signature
  signatureDate: string
  signatureDataUrl: string

  // Collection Details
  collectionDate: string
  collectionTime: string
  collectionAmPm: string
  isFasting: boolean
  urineHrsVol: string

  // Hospital Patient Status
  hospitalPatientStatus: string

  // Lab Facility Selection
  selectedFacilityId: string

  // Provider (auto-populated, hidden)
  organizationName: string
  orgPhone: string
  orgFax: string
  orgAddress: string
  orgCity: string
  orgState: string
  orgZip: string
  orderingProviderName: string
  orderingProviderNpi: string
}

export function createInitialLabcorpFormState(): LabcorpFormState {
  return {
    firstName: '',
    middleInitial: '',
    lastName: '',
    sexAtBirth: '',
    dateOfBirth: '',
    patientId: '',
    patientAddress: '',
    patientPhone: '',
    patientCity: '',
    patientState: '',
    patientZip: '',

    physicianLastName: '',
    physicianFirstName: '',
    physicianNpi: '',
    physicianId: '',

    diagnosisText: '',

    primaryInsuranceCarrier: '',
    primaryInsuranceId: '',
    primaryGroupNumber: '',
    primaryInsuranceAddress: '',
    primaryInsuredName: '',
    primaryRelationship: '',
    primaryEmployerName: '',

    medicaidState: '',
    physiciansProviderNumber: '',
    isWorkersComp: false,

    policyHolderName: '',
    policyHolderAddress: '',
    policyHolderApt: '',
    policyHolderCity: '',
    policyHolderState: '',
    policyHolderZip: '',

    selectedInstructionCodes: [],
    isPstPsc: false,

    selectedTests: [],
    writeInTests: [],

    microbiologySources: [],
    microbiologyOtherSource: '',

    clinicalComments: '',

    signatureDate: '',
    signatureDataUrl: '',

    collectionDate: '',
    collectionTime: '',
    collectionAmPm: 'AM',
    isFasting: false,
    urineHrsVol: '',

    hospitalPatientStatus: '',

    selectedFacilityId: '',

    organizationName: '',
    orgPhone: '',
    orgFax: '',
    orgAddress: '',
    orgCity: '',
    orgState: '',
    orgZip: '',
    orderingProviderName: '',
    orderingProviderNpi: '',
  }
}
